/**
 * Apariencia del sistema (logos, colores y fuente) configurada en
 * Administración → Apariencia. Se aplica al arrancar la aplicación con el
 * tema vigente que devuelve GET /apariencia (público) y vuelve a aplicarse
 * cuando el administrador guarda cambios, sin recargar la página.
 *
 * Si el API falla, todo queda con los valores de fábrica y no se muestra error.
 */
import Vue from 'vue';
import axios from 'axios';

const LOGO_GOBIERNO = require('@/assets/image/logo-gobierno.png');
const LOGO_INFOP = require('@/assets/image/logo-infop-virtual.png');

/** Colores institucionales (los mismos de src/config.js y Login.scss). */
export const COLORES_FABRICA = {
    primario: '#203D89',
    secundario: '#D29E4A',
    acento: '#D29E4A',
    fondo: '#F3F5FA',
    texto: '#1B2340',
};

export const FUENTE_FABRICA = {familia: 'Roboto', google: true};

/** Fuentes que se ofrecen (todas en Google Fonts, salvo "Sistema"). */
export const FUENTES = ['Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 'Nunito', 'Inter', 'Source Sans 3', 'Sistema'];

const PILA_SISTEMA = 'system-ui, -apple-system, "Segoe UI", "Helvetica Neue", Arial, sans-serif';

/** Logos empaquetados con la aplicación: respaldo cuando el tema no trae uno propio. */
export const logosPredeterminados = {
    gobierno: LOGO_GOBIERNO,
    infop: LOGO_INFOP,
    favicon: null,
};

/** Estado reactivo compartido con Sidebar, LoginLayout y la página de administración. */
export const apariencia = Vue.observable({
    cargada: false,
    tema: null,
    version: null,
    colores: {...COLORES_FABRICA},
    fuente: {...FUENTE_FABRICA},
    logos: {gobierno: null, infop: null, favicon: null},
});

let vuetifyActual = null;
let faviconOriginal = null;

/** Raíz del backend (sin /api) para completar las rutas relativas que devuelve el API. */
export function raizBackend() {
    const base = (axios.defaults.baseURL || '').replace(/\/+$/, '');
    return base.replace(/\/api$/, '') || window.location.origin;
}

/** URL absoluta de un recurso que el API devuelve relativo al host ("api/apariencia/logo/..."). */
export function urlAbsoluta(ruta) {
    if (!ruta) return null;
    if (/^(https?:)?\/\//i.test(ruta) || ruta.startsWith('data:') || ruta.startsWith('blob:')) return ruta;
    return `${raizBackend()}/${String(ruta).replace(/^\/+/, '')}`;
}

/** Logo vigente de un tipo (gobierno, infop, favicon), con respaldo al asset empaquetado. */
export function logo(tipo) {
    return apariencia.logos[tipo] || logosPredeterminados[tipo] || null;
}

/** Valor CSS de font-family para una fuente elegida. */
export function pilaFuente(fuente) {
    const familia = fuente && fuente.familia ? fuente.familia : FUENTE_FABRICA.familia;
    if (familia === 'Sistema') return PILA_SISTEMA;
    return `"${familia}", "Roboto", "Helvetica", Arial, sans-serif`;
}

/** Enlace de Google Fonts para la familia indicada (null para "Sistema"). */
export function enlaceFuenteGoogle(familia) {
    if (!familia || familia === 'Sistema') return null;
    return `https://fonts.googleapis.com/css2?family=${encodeURIComponent(familia).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`;
}

/** Inyecta (o reutiliza) el <link> de Google Fonts. Se usa con ids distintos para el tema y la vista previa. */
export function cargarFuenteGoogle(familia, id = 'apariencia-fuente') {
    const href = enlaceFuenteGoogle(familia);
    let enlace = document.getElementById(id);

    if (!href) {
        if (enlace) enlace.remove();
        return;
    }

    if (!enlace) {
        enlace = document.createElement('link');
        enlace.id = id;
        enlace.rel = 'stylesheet';
        document.head.appendChild(enlace);
    }

    if (enlace.href !== href) enlace.href = href;
}

function estiloGlobal() {
    let estilo = document.getElementById('apariencia-estilos');
    if (!estilo) {
        estilo = document.createElement('style');
        estilo.id = 'apariencia-estilos';
        document.head.appendChild(estilo);
    }
    return estilo;
}

function aplicarFavicon(url) {
    const enlace = document.querySelector('link[rel="icon"]');
    if (!enlace) return;
    if (faviconOriginal === null) faviconOriginal = enlace.getAttribute('href') || '';
    enlace.setAttribute('href', url || faviconOriginal);
    if (url) enlace.removeAttribute('type');
}

/**
 * Aplica un tema ya resuelto: colores en Vuetify y en variables CSS de :root,
 * fuente en el documento, logos en el estado compartido y favicon.
 */
export function aplicarTema(datos, vuetify = vuetifyActual) {
    const colores = {...COLORES_FABRICA, ...((datos && datos.colores) || {})};
    const fuente = {...FUENTE_FABRICA, ...((datos && datos.fuente) || {})};
    const logos = (datos && datos.logos) || {};

    apariencia.colores = colores;
    apariencia.fuente = fuente;
    apariencia.logos = {
        gobierno: urlAbsoluta(logos.gobierno),
        infop: urlAbsoluta(logos.infop),
        favicon: urlAbsoluta(logos.favicon),
    };
    apariencia.tema = (datos && datos.tema) || null;
    apariencia.version = (datos && datos.version) || null;
    apariencia.cargada = true;

    if (vuetify && vuetify.framework && vuetify.framework.theme) {
        const claro = vuetify.framework.theme.themes.light;
        claro.primary = colores.primario;
        claro.secondary = colores.secundario;
        claro.accent = colores.acento;
    }

    const raiz = document.documentElement.style;
    raiz.setProperty('--apariencia-primario', colores.primario);
    raiz.setProperty('--apariencia-secundario', colores.secundario);
    raiz.setProperty('--apariencia-acento', colores.acento);
    raiz.setProperty('--apariencia-fondo', colores.fondo);
    raiz.setProperty('--apariencia-texto', colores.texto);
    raiz.setProperty('--apariencia-fuente', pilaFuente(fuente));

    if (fuente.google && fuente.familia !== 'Sistema') {
        cargarFuenteGoogle(fuente.familia);
    } else {
        cargarFuenteGoogle(null);
    }

    // Vuetify fija Roboto en .v-application y en las clases tipográficas; la variable gana.
    estiloGlobal().textContent = `
body, .v-application, .v-application [class*="text-h"], .v-application [class*="text-subtitle"],
.v-application [class*="text-body"], .v-application .text-caption, .v-application .text-button, .v-application .text-overline {
    font-family: var(--apariencia-fuente) !important;
}`;

    aplicarFavicon(apariencia.logos.favicon);
}

/**
 * Pide el tema vigente y lo aplica. La llama main.js antes de montar la aplicación;
 * la página de administración la vuelve a llamar tras guardar.
 */
export function aplicarApariencia(vuetify) {
    if (vuetify) vuetifyActual = vuetify;

    return axios.get('/apariencia')
        .then(response => {
            if (response && response.data && response.data.success && response.data.data) {
                aplicarTema(response.data.data, vuetifyActual);
            }
        })
        .catch(() => {
            // Sin apariencia configurada o API caído: se conservan los valores de fábrica.
        });
}

export function recargarApariencia() {
    return aplicarApariencia(vuetifyActual);
}
